<script lang="ts">
    import { onMount } from "svelte";
    import { Roarr as log } from "roarr";

    import {
        name,
        server,
        players,
        currentPlayer,
        isReady,
        initialTiles,
        initialMeld,
    } from "../store/userdata";
    import { websocket } from "../store/websocket";
    import ChatBox from "../components/ChatBox.svelte";
    import type { ChatMessage } from "../types/chat";
    import { convertNameToColour } from "../misc/nameToColour";
    import { getColourClass } from "../styles/nameColourClasses";
    import { navigateTo } from "../misc/routes";

    let messages: ChatMessage[] = [];

    function onMessageSubmit(e: CustomEvent<string>) {
        const message = e.detail;
        websocket.sendChatMessage(message);

        log.info(`Sent chat message on lobby: ${message}`);
    }

    function onReadyUpClick() {
        websocket.iAmReady();
        $isReady = $isReady.add($name);

        log.info("We have readied up.");
    }

    function onLeaveClick() {
        websocket.close();
        navigateTo("/");
    }

    let playerListCssClasses = {};
    $: {
        for (const player of $players) {
            if ($isReady.has(player)) {
                playerListCssClasses[player] = `${convertNameToCssColourClass(
                    player
                )} bold`;
            } else {
                playerListCssClasses[player] = ``;
            }
        }
    }

    function convertNameToCssColourClass(name: string): string {
        const nameColour = convertNameToColour(name);
        return getColourClass(nameColour);
    }

    onMount(() => {
        websocket.onReceiveChatMessage((senderName, chatMessage) => {
            log.info(
                `Received chat message from ${senderName} in lobby: "${chatMessage}"`
            );
            const message: ChatMessage = {
                name: senderName,
                message: chatMessage,
            };
            messages = [...messages, message];
        });

        websocket.onNewPlayer((newPlayerName) => {
            log.info(`${newPlayerName} has arrived to the server.`);
            $players = [...$players, newPlayerName];
        });

        websocket.onLostPlayer((lostPlayer) => {
            log.info(`${lostPlayer} has left the server.`);
            $players = $players.filter((player) => player != lostPlayer);
        });

        websocket.onTheyAreReady((readyPlayer) => {
            log.info(`${readyPlayer} has readied up.`);
            $isReady = $isReady.add(readyPlayer);
        });

        websocket.onReadyInterrupt(() => {
            $isReady = new Set();
        });

        websocket.onGameStart((tiles, firstPlayer, isOnInitialMeld) => {
            log.info("The game has begun!");
            log.info("Our starting tiles are:");
            console.info(tiles);
            log.info(`${firstPlayer} will go first.`);
            log.info(
                `We are ${isOnInitialMeld ? "" : "not "}on our initial meld.`
            );

            $initialTiles = tiles;
            $currentPlayer = firstPlayer;
            $initialMeld = isOnInitialMeld;

            navigateTo("/game");
        });

        return () => websocket.unsubscribeFromAll();
    });
</script>

<main class="lobby-shell">
    <header class="lobby-header">
        <div>
            <h1>Lobby</h1>
            <p id="serverName">Welcome to {$server}, {$name}.</p>
        </div>
        <div class="lobby-badges">
            <span class="badge">{$players.length} Players</span>
            <span class="badge ready">{$isReady.size} Ready</span>
        </div>
    </header>

    <div id="lobby">
        <section class="panel players-panel">
            <div class="section-head">
                <h2>Players</h2>
                <span class="caption">Waiting room</span>
            </div>

            <div class="players-grid">
                {#each $players as player}
                    <article class={`player-card ${playerListCssClasses[player]}`}>
                        <div>
                            <strong>{player}</strong>
                            <span class="compat-label">{player} {$isReady.has(player) ? '(ready)' : '(not ready)'}</span>
                            <p>{$isReady.has(player) ? 'Ready to deal' : 'Not ready yet'}</p>
                        </div>
                        <span class:ready-dot={$isReady.has(player)} class="status-dot" />
                    </article>
                {/each}
            </div>
        </section>

        <section class="panel queue-panel">
            <div class="section-head">
                <h2>Table Status</h2>
                <span class="caption">Launch when everyone is ready</span>
            </div>

            <p aria-label="please-ready-label" class="status-copy">
                {$isReady.has($name) ? 'Please wait for other players to ready up!' : 'Please ready up!'}
            </p>

            <div class="lobby-actions">
                <button
                    id="readyBtn"
                    aria-label="ready-btn"
                    class="primary-action"
                    disabled={$isReady.has($name)}
                    on:click={onReadyUpClick}
                >Ready up</button>

                <button
                    id="leaveBtn"
                    aria-label="leave-btn"
                    class="leave-action"
                    on:click={onLeaveClick}
                >Leave</button>
            </div>
        </section>

        <section class="panel chat-panel">
            <div class="section-head">
                <h2>Table Chat</h2>
                <span class="caption">Plan, taunt, negotiate</span>
            </div>
            <ChatBox bind:messages on:submitMessage={onMessageSubmit} />
        </section>
    </div>
</main>

<style>
    .lobby-shell {
        min-height: 100vh;
        padding: 24px;
        display: grid;
        gap: 18px;
    }

    .lobby-header {
        display: flex;
        justify-content: space-between;
        align-items: end;
        gap: 16px;
    }

    .lobby-header h1,
    #serverName,
    h2 {
        margin: 0;
    }

    .lobby-header h1 {
        font-size: clamp(2.4rem, 5vw, 3.6rem);
        font-family: var(--title-font);
    }

    #serverName {
        color: var(--text-muted);
        margin-top: 8px;
    }

    .lobby-badges {
        display: flex;
        gap: 10px;
        flex-wrap: wrap;
    }

    .badge {
        padding: 0.7rem 0.95rem;
        border-radius: 999px;
        background: rgba(17, 36, 64, 0.82);
        border: 1px solid rgba(123, 162, 222, 0.22);
        color: var(--text-muted);
    }

    .badge.ready {
        color: #071522;
        background: linear-gradient(180deg, #a6ff80, #62d96a);
        border-color: rgba(166, 255, 128, 0.4);
    }

    #lobby {
        display: grid;
        grid-template-columns: 1.15fr 0.85fr;
        grid-template-areas:
            "players queue"
            "chat chat";
        gap: 18px;
        min-height: 0;
    }

    .panel {
        border-radius: 28px;
        border: 1px solid rgba(126, 173, 255, 0.16);
        background:
            radial-gradient(circle at top right, rgba(67, 121, 255, 0.09), transparent 28%),
            linear-gradient(180deg, rgba(12, 28, 50, 0.94), rgba(7, 16, 31, 0.95));
        box-shadow: var(--shadow-soft);
        padding: 22px;
    }

    .players-panel {
        grid-area: players;
    }

    .queue-panel {
        grid-area: queue;
    }

    .chat-panel {
        grid-area: chat;
    }

    .section-head {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        gap: 10px;
        margin-bottom: 18px;
    }

    .caption {
        color: var(--text-dim);
        font-size: 0.78rem;
        text-transform: uppercase;
        letter-spacing: 0.12em;
    }

    .players-grid {
        display: grid;
        gap: 12px;
    }

    .player-card {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 12px;
        padding: 16px 18px;
        border-radius: 18px;
        border: 1px solid rgba(123, 162, 222, 0.18);
        background: rgba(17, 36, 64, 0.7);
    }

    .player-card strong {
        display: block;
        margin-bottom: 4px;
    }

    .player-card p {
        margin: 0;
        color: var(--text-muted);
    }

    .compat-label {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
    }

    .status-dot {
        width: 12px;
        height: 12px;
        border-radius: 999px;
        background: rgba(255, 118, 111, 0.6);
        box-shadow: 0 0 0 6px rgba(255, 118, 111, 0.08);
    }

    .ready-dot {
        background: rgba(115, 235, 124, 0.9);
        box-shadow: 0 0 0 6px rgba(115, 235, 124, 0.08);
    }

    .status-copy {
        margin: 8px 0 22px;
        color: var(--text-main);
        line-height: 1.6;
        font-size: 1.1rem;
    }

    .lobby-actions {
        display: grid;
        gap: 12px;
    }

    .primary-action {
        background: linear-gradient(180deg, rgba(71, 134, 255, 0.96), rgba(25, 75, 158, 0.98));
    }

    .leave-action {
        background: rgba(68, 18, 25, 0.9);
        border-color: rgba(255, 118, 111, 0.24);
        color: #ffb0ac;
    }

    .bold {
        border-color: rgba(115, 235, 124, 0.36);
        box-shadow: 0 0 0 1px rgba(115, 235, 124, 0.12) inset;
    }

    @media (max-width: 900px) {
        .lobby-shell {
            padding: 16px;
        }

        .lobby-header {
            flex-direction: column;
            align-items: flex-start;
        }

        #lobby {
            grid-template-columns: 1fr;
            grid-template-areas:
                "players"
                "queue"
                "chat";
        }
    }
</style>
